/**
 * SettingsTab — Configuración del módulo de Nómina a nivel tenant.
 * Recordatorio mensual, aviso de contratos, segundo aprobador y beneficios de días.
 */
import { useEffect, useState } from 'react'

import { Button } from '@/components/Button'
import { useToast } from '@/components/Toast'
import { usePermission } from '@/hooks/usePermission'

import { useEmployees, usePayrollConfig, useUpdatePayrollConfig } from '../hooks/usePayroll'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { BeneficiosDeDiasSection } from './BeneficiosDeDiasSection'


export function SettingsTab() {
  const { data, isLoading } = usePayrollConfig()
  const update = useUpdatePayrollConfig()
  const toast = useToast()
  const { can } = usePermission()
  const canEdit = can('payroll', 'edit')

  const [reminderDay, setReminderDay] = useState<number>(20)
  const [reminderEnabled, setReminderEnabled] = useState<boolean>(true)
  const [contractAlertEnabled, setContractAlertEnabled] = useState<boolean>(true)
  const [contractAlertDays, setContractAlertDays] = useState<number>(15)
  const [segundoAprobador, setSegundoAprobador] = useState<string>('')
  const { data: empData } = useEmployees({ active: 'true', page_size: 9999 })
  const fichas: { id: string; full_name: string }[] = empData?.employees || []

  useEffect(() => {
    if (data) {
      setReminderDay(data.reminder_day)
      setReminderEnabled(data.reminder_enabled)
      setContractAlertEnabled(data.contract_expiry_alert_enabled ?? true)
      setContractAlertDays(data.contract_expiry_alert_days ?? 15)
      setSegundoAprobador(data.vacaciones_segundo_aprobador ?? '')
    }
  }, [data])

  const handleSave = async () => {
    if (reminderDay < 1 || reminderDay > 28) {
      toast.error('El día debe estar entre 1 y 28.')
      return
    }
    if (contractAlertDays < 1 || contractAlertDays > 120) {
      toast.error('Los días de aviso de contrato deben estar entre 1 y 120.')
      return
    }
    try {
      await update.mutateAsync({
        reminder_day: reminderDay,
        reminder_enabled: reminderEnabled,
        contract_expiry_alert_enabled: contractAlertEnabled,
        contract_expiry_alert_days: contractAlertDays,
        vacaciones_segundo_aprobador: segundoAprobador || null,
      })
      toast.success('Configuración guardada.')
    } catch (exc: any) {
      toast.error(exc?.response?.data?.error?.message || 'Error al guardar.')
    }
  }

  if (isLoading) return <div className="text-sm text-content-muted">Cargando…</div>

  return (
    <div className="max-w-2xl space-y-6">
      <section className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
        <h3 className="text-sm font-semibold text-content mb-1">Recordatorio mensual</h3>
        <p className="text-xs text-content-muted mb-4">
          Se envía a los administradores del tenant si aún no se ha calculado la nómina del mes.
          Se ejecuta a las 09:00 UTC del día seleccionado.
        </p>

        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm text-content">
            <input
              type="checkbox"
              checked={reminderEnabled}
              onChange={e => setReminderEnabled(e.target.checked)}
              disabled={!canEdit}
              className="rounded border-divider-strong"
            />
            Enviar recordatorio
          </label>

          <TextField
            dense
            label="Día del mes (1-28)"
            value={reminderDay}
            onChange={e => setReminderDay(parseInt(e.target.value, 10) || 1)}
            disabled={!canEdit || !reminderEnabled}
            inputProps={{ type: 'number', min: 1, max: 28 }}
            hint="Limitado a 28 para evitar meses con menos días."
          />
        </div>

      </section>

      <section className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
        <h3 className="text-sm font-semibold text-content mb-1">Contratos a plazo fijo por vencer</h3>
        <p className="text-xs text-content-muted mb-4">
          Avisa a RRHH/Administración cuando un contrato a plazo fijo está por vencer. Recuerda que la
          2ª renovación lo transforma en indefinido, y que si el trabajador sigue prestando servicios tras
          el vencimiento también pasa a indefinido.
        </p>

        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm text-content">
            <input
              type="checkbox"
              checked={contractAlertEnabled}
              onChange={e => setContractAlertEnabled(e.target.checked)}
              disabled={!canEdit}
              className="rounded border-divider-strong"
            />
            Avisar contratos por vencer
          </label>

          <TextField
            dense
            label="Días de anticipación"
            value={contractAlertDays}
            onChange={e => setContractAlertDays(parseInt(e.target.value, 10) || 1)}
            disabled={!canEdit || !contractAlertEnabled}
            inputProps={{ type: 'number', min: 1, max: 120 }}
            hint="Por defecto 15 días. Se revisa diariamente a las 08:00 UTC."
          />
        </div>
      </section>

      <section className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
        <h3 className="text-sm font-semibold text-content mb-1">Segundo aprobador</h3>
        <p className="text-xs text-content-muted mb-4">
          Con una persona elegida, las vacaciones —y los beneficios de días que lo pidan— necesitan dos
          aprobaciones: la primera de la jefatura (o de RR. HH.) y la segunda de esta persona, desde
          «Mi equipo» del portal. Se le pide recién cuando la primera está aprobada, y los días se
          descuentan del saldo con la segunda. Si quien aprueba primero es esta misma persona, su
          aprobación basta. Los demás permisos siguen con una sola aprobación.
        </p>
        <SelectField
          dense
          label="Persona que da la segunda aprobación"
          value={segundoAprobador}
          onChange={e => setSegundoAprobador(e.target.value)}
          disabled={!canEdit}
          placeholder="Sin segunda aprobación"
          options={fichas.map(f => ({ value: f.id, label: f.full_name }))}
          hint={segundoAprobador
            ? 'Necesita cuenta en el portal del colaborador para aprobar.'
            : 'Las vacaciones y los beneficios se aprueban con una sola aprobación.'}
        />
      </section>

      {canEdit && (
        <div>
          <Button onClick={handleSave} isPending={update.isPending} pendingLabel="Guardando…">
            Guardar cambios
          </Button>
        </div>
      )}

      {/* Los beneficios se guardan solos (cada uno con su modal), por eso van
          después del botón de la configuración general. */}
      <BeneficiosDeDiasSection canEdit={canEdit} />
    </div>
  )
}
